/**
 * Lazy editor exports — sl-e0il.1.3
 *
 * Wraps `CodeEditor`, `TemplateEditor` and `TemplateInput` in
 * `React.lazy` so CodeMirror 6 (plus the three language grammars)
 * ships in a separate chunk. Consumers render the returned component
 * directly — `Suspense` fallback and `EditorErrorBoundary` are built
 * in, so config panels don't have to wire them up at every call site.
 *
 * The wrappers use `forwardRef` to preserve the `EditorAdapter` ref
 * contract — `<LazyCodeEditor ref={adapterRef} ... />` still exposes
 * `insertAtCursor` once the chunk has loaded.
 */

import { forwardRef, lazy, Suspense } from 'react';

import type { CodeEditorProps } from './code-editor';
import type { EditorAdapter } from './editor-adapter';
import { EditorErrorBoundary } from './editor-error-boundary';
import { EditorLoadingSkeleton } from './editor-loading-skeleton';
import type { TemplateEditorProps } from './template-editor';
import type { TemplateInputProps } from './template-input';

const CodeEditorLazy = lazy(() =>
  import('./code-editor').then(m => ({ default: m.CodeEditor }))
);

const TemplateEditorLazy = lazy(() =>
  import('./template-editor').then(m => ({ default: m.TemplateEditor }))
);

const TemplateInputLazy = lazy(() =>
  import('./template-input').then(m => ({ default: m.TemplateInput }))
);

export const LazyCodeEditor = forwardRef<EditorAdapter, CodeEditorProps>(
  function LazyCodeEditor(props, ref) {
    return (
      <EditorErrorBoundary>
        <Suspense fallback={<EditorLoadingSkeleton variant="block" />}>
          <CodeEditorLazy ref={ref} {...props} />
        </Suspense>
      </EditorErrorBoundary>
    );
  }
);

export const LazyTemplateEditor = forwardRef<
  EditorAdapter,
  TemplateEditorProps
>(function LazyTemplateEditor(props, ref) {
  return (
    <EditorErrorBoundary>
      <Suspense fallback={<EditorLoadingSkeleton variant="block" />}>
        <TemplateEditorLazy ref={ref} {...props} />
      </Suspense>
    </EditorErrorBoundary>
  );
});

export const LazyTemplateInput = forwardRef<EditorAdapter, TemplateInputProps>(
  function LazyTemplateInput(props, ref) {
    return (
      <EditorErrorBoundary>
        <Suspense fallback={<EditorLoadingSkeleton variant="inline" />}>
          <TemplateInputLazy ref={ref} {...props} />
        </Suspense>
      </EditorErrorBoundary>
    );
  }
);
